import { Box, Eye, Package2, Pencil, Trash, User } from "lucide-react"  
import { MarketResearch, ResearchedProduct } from '@/types/MarketResearch' 
import { publishResearchedProduct } from "@/api/business/marketResearch"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useToast } from "@/components/ui/use-toast"
import { isAxiosError } from '@/api/business'
import { ResearchedProductKeys } from "@/queries/useResearchedProductQuery"
import { getLocaleDate } from "@/utils/date" 

interface props {
    marketResearch: MarketResearch,
    dataResearchedProducts: ResearchedProduct,
    handleViewImage: (key: string) => void
    handleEditProduct: (product: ResearchedProduct) => void
    handleDeleteProduct: (product: ResearchedProduct) => void
}

const CardsResearchedProducts = ({marketResearch, dataResearchedProducts, handleViewImage, handleEditProduct, handleDeleteProduct }: props) => {    
    const { toast } = useToast()  
    const queryClient = useQueryClient()

    const { mutate, isPending } = useMutation({
        mutationFn: publishResearchedProduct,
        onSuccess: () => {  
            queryClient.invalidateQueries({ queryKey: ResearchedProductKeys.details() })
            toast({
                title: 'Mudança do status da pesquisa efetuado com sucesso',
            })
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível efetuar a mudança do status. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao excluir efetuar a mudança do status',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    }) 
    
    return (  
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300 h-auto">  
                <div className="p-3"> 
    
                    <div className="space-y-2"> 
                        <div className="flex gap-1.5">
                            <User className='text-gray-600' size={14} />
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-900 font-bold truncate">{dataResearchedProducts.DS_NOME_FANTASIA}</p>
                            </div>
                        </div>

                        <div className="flex gap-1.5">
                            <Box className='text-gray-600' size={14} />
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-900 font-medium truncate ">{dataResearchedProducts.DS_PRODUTO}</p>
                            </div>
                        </div>  
    
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                            <div>
                                <p className="text-xs text-gray-500 mb-0.5">Compra Cx.</p>
                                <p className="text-sm font-bold text-red-600">
                                    R$ {(dataResearchedProducts.VL_PRECO_COMPRA).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-0.5">Venda un.</p>
                                <p className="text-sm font-bold text-green-600">
                                    R$ {(dataResearchedProducts.VL_PRECO_VENDA).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                            </div>
                        </div>
    
                         <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                            <div className="flex gap-1.5">
                                <Package2  className='text-gray-600 mt-0.5' size={14} />
                                <p className="text-xs text-gray-900 font-semibold">{dataResearchedProducts.QTD_VOLUME_COMPRA} Cx.</p>
                            </div> 
                            {dataResearchedProducts.DS_CHAVE_ARQUIVO && (
                                <div className="flex gap-1.5">
                                    <Eye  className='text-blue-600 mt-0.5' size={14} />
                                    <button onClick={() => handleViewImage(dataResearchedProducts.DS_CHAVE_ARQUIVO)} className="text-xs text-blue-600 font-semibold">Visualizar imagem</button>
                                </div> 
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                            <div>
                                <p className="text-xs text-gray-500 mb-0.5">Status</p>
                                <p className="text-sm text-gray-900 font-semibold">
                                    {dataResearchedProducts.DD_STATUS}
                                </p>
                            </div>
                            {!dataResearchedProducts.DT_PUBLICACAO && (
                                <div className="flex items-center justify-end">
                                    <button onClick={() => handleDeleteProduct(dataResearchedProducts)} >
                                        <Trash size={18} className="text-red-700 cursor-pointer"/>
                                    </button>
                                </div>
                            )}
                            {dataResearchedProducts.DT_PUBLICACAO && (
                                <div>
                                    <p className="text-xs text-gray-500 mb-0.5">Data publicação</p>
                                    <p className="text-sm text-gray-900 font-semibold">
                                        {getLocaleDate(dataResearchedProducts.DT_PUBLICACAO, 'dd/MM/yyyy HH:mm')}
                                    </p>
                                </div>
                            )}
                        </div>

                        {(['Publicado'].includes(marketResearch.DD_STATUS) && dataResearchedProducts.DD_STATUS == 'Lançado') && (
                            <div className="flex flex-1 gap-4 pt-2 border-t border-gray-100">
                                <button 
                                    className="w-full btn-secondary border font-medium py-2 px-0.5 rounded-lg flex items-center justify-center text-sm gap-1.5"  
                                    onClick={() => handleEditProduct(dataResearchedProducts)}
                                >
                                    <Pencil size={18}/>
                                    Editar
                                </button> 
                                <button 
                                    className="w-full btn-primary text-white font-medium py-2 px-0.5 rounded-lg flex items-center justify-center text-sm gap-1.5"  
                                    onClick={() => mutate(dataResearchedProducts.SK_PRODUTOS_PESQUISADOS)}
                                >  
                                    {isPending ? 'Publicando...' : 'Publicar'}
                                </button> 
                            </div> 
                        )}
                    </div>
                </div> 
            </div> 
    )
}

export default CardsResearchedProducts
